import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LanguageVO, SentenceCollectionVO } from '../models_generated';
import { save, saveAll, loadAll } from '../utils/dao';
import { DBCollection, DBSentence, SentenceStatus, Table } from '../models/db_models';
import useObservableListener from '../utils/useObservableListener';
import { BASE_URL, SENTENCES_STORE } from '../stores/SentencesStore';
import { useAsyncEffect } from '../utils';

function Home() {
  const navigate = useNavigate();
  const languages = useObservableListener<LanguageVO[]>(SENTENCES_STORE.languages);
  const collections = useObservableListener<SentenceCollectionVO[]>(SENTENCES_STORE.sentenceCollections);
  const loaded = useObservableListener<boolean>(SENTENCES_STORE.done);
  const [downloadedCollections, setDownloadedCollections] = useState<Set<string>>(new Set());
  const [downloadedCollectionData, setDownloadedCollectionData] = useState<Map<string, DBCollection>>(new Map());

  useAsyncEffect(async () => {
      const downloaded = await loadAll(Table.COLLECTIONS) as DBCollection[];
      const downloadedIds = new Set(downloaded.map(col => col.id));
      const downloadedMap = new Map(downloaded.map(col => [col.id, col]));
      setDownloadedCollections(downloadedIds);
      setDownloadedCollectionData(downloadedMap);
  }, [languages, collections]);

  if (!loaded) {
    return (
      <div className="container text-center mt-5">
        <img src={process.env.PUBLIC_URL + '/ic_launcher.png'} alt="10,000 Sentences" style={{ width: 128, height: 128 }} className="mb-4" />
        <div className="spinner-border d-block mx-auto" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading languages...</p>
      </div>
    );
  }

  const getLanguageName = (abbrev: string): string => {
    const lang = languages.find(l => l.abbrev === abbrev || l.abbrev3 === abbrev);
    if (!lang) return abbrev;

    // If name and nativeName are the same, show only one
    if (lang.name === lang.nativeName) {
      return lang.name;
    }

    return `${lang.name} (${lang.nativeName})`;
  };

  const getSortedCollections = (): SentenceCollectionVO[] => {
    return [...collections.slice()].sort((a, b) => {
      const aDownloaded = downloadedCollections.has(a.filename);
      const bDownloaded = downloadedCollections.has(b.filename);

      // Downloaded collections first
      if (aDownloaded && !bDownloaded) return -1;
      if (!aDownloaded && bDownloaded) return 1;

      if (aDownloaded && bDownloaded) {
        // Both downloaded: sort by progress percentage (lower first)
        const aData = downloadedCollectionData.get(a.filename);
        const bData = downloadedCollectionData.get(b.filename);
        const aProgress = aData ? (aData.doneCount / a.count) : 0;
        const bProgress = bData ? (bData.doneCount / b.count) : 0;
        return aProgress - bProgress;
      }

      // Both not downloaded: sort by target language, then known language
      const aTarget = getLanguageName(a.targetLanguage);
      const bTarget = getLanguageName(b.targetLanguage);
      if (aTarget !== bTarget) {
        return aTarget.localeCompare(bTarget);
      }
      return getLanguageName(a.knownLanguage).localeCompare(getLanguageName(b.knownLanguage));
    });
  };

  const handleCollectionClick = async (col: SentenceCollectionVO) => {
    // If already downloaded, navigate to the collection page
    if (downloadedCollections.has(col.filename)) {
      navigate(`/collection/${col.filename}`);
      return;
    }

    // Ask for confirmation before downloading
    const confirmed = window.confirm(
      `Download "${getLanguageName(col.knownLanguage)} → ${getLanguageName(col.targetLanguage)}"?\n\n` +
      `This will download ${col.count.toLocaleString()} sentences.`
    );

    if (!confirmed) {
      return;
    }

    // Download and import
    try {
      console.log('Downloading collection:', col.filename);

      // Download the file
      const response = await fetch(`${BASE_URL}/${col.filename}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      const lines = text.split('\n').filter(line => line.trim().length > 0);

      console.log(`Parsing ${lines.length} sentences...`);

      // Parse sentences
      const sentences: DBSentence[] = lines.map((line, index) => {
        const parts = line.split('\t');
        const sentence = new DBSentence();
        sentence.id = parts[0];
        sentence.order = index + 1;
        sentence.collectionId = col.filename;
        sentence.knownSentence = parts[1] || '';
        sentence.targetSentence = parts[2] || '';
        sentence.complexity = parseFloat(parts[3]) || 0;
        sentence.status = SentenceStatus.TODO;
        return sentence;
      });

      // Save collection metadata
      const dbCollection = new DBCollection();
      dbCollection.id = col.filename;
      dbCollection.knownLanguage = col.knownLanguage;
      dbCollection.targetLanguage = col.targetLanguage;
      dbCollection.type = col.type;
      dbCollection.count = col.count;
      dbCollection.downloadedAt = Date.now();
      dbCollection.doneCount = 0;
      dbCollection.todoCount = sentences.length;
      dbCollection.repeatCount = 0;

      console.log('Saving to database...');
      await save(dbCollection);
      await saveAll(sentences);

      console.log('Import complete!');

      // Navigate to the collection page
      navigate(`/collection/${col.filename}`);
    } catch (err) {
      console.error('Import failed:', err);
      alert(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="container py-4">
      <h1 className="text-center mb-3">10,000 Sentences</h1>
      <h2 className="text-center mb-4">Available Language Pairs</h2>

      <div className="list-group mx-auto" style={{ maxWidth: '700px' }}>
        {getSortedCollections().map((col) => {
          const isDownloaded = downloadedCollections.has(col.filename);
          return (
            <div
              key={col.filename}
              className={`list-group-item list-group-item-action ${isDownloaded ? 'list-group-item-success' : ''}`}
              onClick={() => handleCollectionClick(col)}
              style={{ cursor: 'pointer' }}
            >
              <div className="fs-5 mb-2">
                Learning <strong>{getLanguageName(col.targetLanguage)}</strong> from {getLanguageName(col.knownLanguage)}.
              </div>
              {isDownloaded && (
                <div className="text-success mb-2">
                  Done: {downloadedCollectionData.get(col.filename)?.doneCount || 0} / {col.count > 10000 ? '10,000+' : col.count.toLocaleString()}
                  {' '}({(((downloadedCollectionData.get(col.filename)?.doneCount || 0) / Math.min(col.count, 10000)) * 100).toFixed(1)}%)
                </div>
              )}
              <small className="text-muted">
                {col.count.toLocaleString()} sentences | Type: {col.type}
                {isDownloaded ? ' | Click to view' : ' | Click to download'}
              </small>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Home;
